import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BasicInfoForm from '../BasicInfoForm';
import VariantsConfig from '../VariantsConfig';
import SettingsConfig from '../SettingsConfig';
import TestSummary from '../TestSummary';

const TestConfigWrapper = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const testType = searchParams.get('type') || 'element';
    const [currentStep, setCurrentStep] = useState(1);

    const [formData, setFormData] = useState({
        basicInfo: null,
        variants: null,
        settings: null,
    });

    const testTypeLabels = {
        pricing: 'Pricing Test',
        product: 'Product Test',
        cart: 'Cart & Discount Test',
        element: 'Element Test',
        shipping: 'Shipping Test',
        checkout: 'Checkout Test',
    };

    const steps = [
        { id: 1, title: 'Basic Info' },
        { id: 2, title: 'Variants' },
        { id: 3, title: 'Settings' },
        { id: 4, title: 'Summary' }
    ];

    const stepDescriptions = {
        1: 'Set up the basic information for your A/B test',
        2: 'Configure your test variants',
        3: 'Set traffic distribution and targeting',
        4: 'Review and launch your test'
    };

    const handleFormDataUpdate = (data) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    const handleNext = () => {
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        } else {
            navigate('/abtest/create');
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <BasicInfoForm
                        testType={testType}
                        formData={formData}
                        onUpdate={handleFormDataUpdate}
                        onNext={handleNext}
                    />
                );
            case 2:
                return (
                    <VariantsConfig
                        testType={testType}
                        formData={formData}
                        onUpdate={handleFormDataUpdate}
                        onNext={handleNext}
                    />
                );
            case 3:
                return (
                    <SettingsConfig
                        testType={testType}
                        formData={formData}
                        onUpdate={handleFormDataUpdate}
                        onNext={handleNext}
                    />
                );
            case 4:
                return (
                    <TestSummary
                        testType={testType}
                        formData={formData}
                        onBack={() => setCurrentStep(3)}
                        onUpdate={handleFormDataUpdate}
                    />
                );
            default:
                return null;
        }
    };

    const isStepCompleted = (stepId) => {
        switch (stepId) {
            case 1:
                return formData.basicInfo !== null;
            case 2:
                return formData.variants !== null;
            case 3:
                return formData.settings !== null;
            case 4:
                return formData.basicInfo !== null &&
                    formData.variants !== null &&
                    formData.settings !== null;
            default:
                return false;
        }
    };

    const canAccessStep = (stepId) => {
        switch (stepId) {
            case 1:
                return true;
            case 2:
                return formData.basicInfo !== null;
            case 3:
                return formData.basicInfo !== null && formData.variants !== null;
            case 4:
                return formData.basicInfo !== null &&
                    formData.variants !== null &&
                    formData.settings !== null;
            default:
                return false;
        }
    };

    const handleStepClick = (stepId) => {
        if (canAccessStep(stepId)) {
            setCurrentStep(stepId);
        }
    };

return (
    <div className="min-h-screen bg-gray-50 px-6 py-8 max-md:px-4 max-md:py-5">
        <div className="max-w-6xl mx-auto">
            {/* Header - Back Button and Centered Title */}
            <div className="relative flex items-center justify-center mb-10 max-md:mb-6">
                <button 
                    onClick={handleBack} 
                    className="absolute left-0 inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 text-sm font-medium transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
                >
                    <ArrowLeft size={18} />
                    Back
                </button>

                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 max-md:text-2xl">
                        Create {testTypeLabels[testType] || 'A/B Test'}
                    </h1>
                    <p className="text-gray-600">
                        {stepDescriptions[currentStep]}
                    </p>
                </div>
            </div>

            {/* Progress Steps */}
            <div className="mb-10 max-md:mb-6">
                <div className="flex items-center justify-between max-w-3xl mx-auto">
                    {steps.map((step, index) => (
                        <React.Fragment key={step.id}>
                            <div 
                                className="flex flex-col items-center gap-2 cursor-pointer"
                                onClick={() => handleStepClick(step.id)}
                            >
                                <div className={`
                                    w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm 
                                    transition-all duration-300 border-2
                                    ${currentStep === step.id 
                                        ? 'bg-blue-500 text-white border-blue-500 scale-110' 
                                        : isStepCompleted(step.id)
                                            ? 'bg-green-500 text-white border-green-500'
                                            : 'bg-gray-100 text-gray-400 border-gray-200'
                                    }
                                    ${canAccessStep(step.id) ? 'hover:scale-105' : 'cursor-not-allowed opacity-50'}
                                `}>
                                    {isStepCompleted(step.id) && currentStep !== step.id ? '✓' : step.id}
                                </div>
                                <span className={`
                                    text-sm font-medium transition-colors duration-300 max-md:text-xs
                                    ${currentStep === step.id 
                                        ? 'text-blue-600 font-semibold' 
                                        : isStepCompleted(step.id)
                                            ? 'text-green-600'
                                            : 'text-gray-500'
                                    }
                                `}>
                                    {step.title}
                                </span>
                            </div>
                            
                            {index < steps.length - 1 && (
                                <div className={`
                                    flex-1 h-0.5 mx-4 transition-all duration-300 max-md:mx-2
                                    ${isStepCompleted(step.id) ? 'bg-green-500' : 'bg-gray-200'}
                                `} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Step Content */}
            <div>
                {renderStepContent()}
            </div>
        </div>
    </div>
);
};

export default TestConfigWrapper;